import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useEditor, EditorContent } from '@tiptap/react'
import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import BaseImage from '@tiptap/extension-image'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { FontFamily } from '@tiptap/extension-font-family'
import { FontSize } from '@tiptap/extension-font-size'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import PaginationExtension, {
  PageNode,
  HeaderFooterNode,
  BodyNode,
} from 'tiptap-extension-pagination'
import {
  Undo,
  Redo,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List as ListIcon,
  ListOrdered,
  IndentDecrease,
  IndentIncrease,
  Link as LinkIcon,
  RemoveFormatting,
  Type,
  ChevronDown,
  Printer,
  Download,
  Share2,
  Image as ImageIcon,
  Minus,
  Calendar as CalendarIcon,
  Maximize,
  Scissors,
  Copy as CopyIcon,
  ClipboardPaste,
  CheckSquare,
  Search,
  Hash,
  Table as TableIcon,
  Trash2,
} from 'lucide-react'
import { formatDateTime } from '../lib/format'
import { copyText } from '../lib/clipboard'

/**
 * Doc editor backed by Tiptap (ProseMirror under the hood). Persists body
 * as `{ html: string }` so the existing `documents.body jsonb` column needs
 * no schema change. Legacy `{ text }` bodies are converted to paragraphs on
 * load and re-saved as `{ html }` on the next edit.
 */

/**
 * Inline extension that adds an `indent` integer attribute to paragraphs
 * and headings, rendered as `margin-left`. Tiptap doesn't ship paragraph
 * indentation out of the box. Toolbar handlers call `updateAttributes`
 * directly — no custom commands needed (avoids chain-typing pain).
 */
const INDENT_STEP = 24 // px per level
const INDENT_MAX = 8
const INDENT_TYPES = ['paragraph', 'heading']
const Indent = Extension.create({
  name: 'indent',
  addGlobalAttributes() {
    return [
      {
        types: [...INDENT_TYPES],
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element) => {
              const ml = parseInt(element.style.marginLeft || '0', 10) || 0
              return Math.max(0, Math.round(ml / INDENT_STEP))
            },
            renderHTML: (attrs) => {
              if (!attrs.indent) return {}
              return { style: `margin-left: ${attrs.indent * INDENT_STEP}px` }
            },
          },
        },
      },
    ]
  },
})

/**
 * Extend Tiptap's Image so it carries optional `width` and `height`
 * attributes — rendered as inline width/height attributes on the <img>
 * tag so existing browser sizing applies. Both are stored as strings so
 * percentages ("50%") and pixel values ("320px") both work.
 */
const Image = BaseImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => el.getAttribute('width'),
        renderHTML: (attrs) =>
          attrs.width ? { width: attrs.width } : {},
      },
      height: {
        default: null,
        parseHTML: (el) => el.getAttribute('height'),
        renderHTML: (attrs) =>
          attrs.height ? { height: attrs.height } : {},
      },
    }
  },
})

/**
 * Strip trailing empty pages from the doc after every transaction. The
 * pagination extension creates page-2 when content overflows page-1, but
 * doesn't always remove that trailing page when content shrinks back.
 *
 * "Empty" here means: page → body → at most one paragraph that has no text.
 * We remove only **one** empty trailing page per transaction to avoid loops
 * when the user is actively typing on a fresh page.
 */
const TRIM_KEY = new PluginKey('trimEmptyTrailingPage')
const TrimEmptyTrailingPage = Extension.create({
  name: 'trimEmptyTrailingPage',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: TRIM_KEY,
        appendTransaction(transactions, _oldState, newState) {
          if (!transactions.some((t) => t.docChanged)) return null
          // Skip if this transaction was generated by the paginator itself
          // — otherwise we fight it on every keystroke.
          if (transactions.some((t) => t.getMeta('pagination'))) return null

          const { doc } = newState
          // Collect page nodes with their start positions.
          const pages = []
          let cursor = 0
          doc.forEach((node) => {
            if (node.type.name === 'page') {
              pages.push({ node, pos: cursor })
            }
            cursor += node.nodeSize
          })
          if (pages.length < 2) return null
          const last = pages[pages.length - 1]
          const lastPage = last.node
          if (!lastPage) return null

          // Inspect the body of the last page.
          // The closure capture in `lastPage.forEach` makes `body` start
          // as null and narrow to the matched node; we keep runtime
          // safety with the explicit null check just below.
          let body = null
          lastPage.forEach((child) => {
            if (child.type.name === 'body') body = child
          })
          if (!body) return null

          // "Empty" body = no children OR only empty paragraph(s) with no text.
          let hasText = false
          body.forEach((child) => {
            if (child.textContent.trim().length > 0) hasText = true
          })
          if (hasText) return null

          // Only trim if the active selection is NOT inside the last page —
          // otherwise we'd yank the user's cursor away while they're typing.
          const selPos = newState.selection.from
          if (selPos >= last.pos && selPos <= last.pos + lastPage.nodeSize) return null

          // Remove the trailing empty page.
          const tr = newState.tr.delete(last.pos, last.pos + lastPage.nodeSize)
          tr.setMeta('trimEmptyTrailingPage', true)
          return tr
        },
      }),
    ]
  },
})

/**
 * Prompt for an image's width and height, then update the selected image
 * node. Both fields accept px values (e.g. "320") or percentages ("50%").
 * Empty values reset that dimension to the original.
 */
function promptImageSize(editor) {
  const attrs = editor.getAttributes('image')
  const width = window.prompt('Width (e.g. 320 or 50%)', attrs.width ?? '')
  if (width === null) return
  const height = window.prompt('Height (e.g. 240 or auto). Leave blank for auto.', attrs.height ?? '')
  if (height === null) return
  const cleanedWidth = width.trim() || null
  const cleanedHeight = height.trim() || null
  editor
    .chain()
    .focus()
    .updateAttributes('image', { width: cleanedWidth, height: cleanedHeight })
    .run()
}

/** Bump the current block's indent by `delta`, returning whether anything changed. */
function bumpIndent(editor, delta) {
  const parent = editor.state.selection.$from.parent
  const name = parent.type.name
  if (!INDENT_TYPES.includes(name)) return false
  const current = parent.attrs.indent ?? 0
  const next = Math.max(0, Math.min(INDENT_MAX, current + delta))
  if (next === current) return false
  return editor
    .chain()
    .focus()
    .updateAttributes(name, { indent: next })
    .run()
}

const STYLE_OPTIONS = [
  { label: 'Normal text', level: 0, size: 14 },
  { label: 'Heading 1', level: 1, size: 26 },
  { label: 'Heading 2', level: 2, size: 20 },
  { label: 'Heading 3', level: 3, size: 16 },
]

/** Show Mac symbols on macOS, Ctrl+/Shift+ everywhere else. */
const IS_MAC =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '')
const MOD = IS_MAC ? '⌘' : 'Ctrl+'
const SHIFT = IS_MAC ? '⇧' : 'Shift+'
function shortcut(key, withShift = false) {
  return withShift ? `${MOD}${SHIFT}${key}` : `${MOD}${key}`
}

const FONT_FAMILIES = [
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Inter', value: 'Inter, system-ui, sans-serif' },
  { label: 'Roboto', value: 'Roboto, Arial, sans-serif' },
  { label: 'Calibri', value: 'Calibri, Candara, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Courier New', value: '"Courier New", monospace' },
]

const FONT_SIZES = ['9px', '10px', '11px', '12px', '14px', '16px', '18px', '24px', '30px', '36px', '48px']

const TEXT_COLOURS = [
  '#0f1b35',
  '#374151',
  '#6B7280',
  '#EF4444',
  '#F59E0B',
  '#10B981',
  '#0EA5E9',
  '#A855F7',
  '#EC4899',
  '#FFFFFF',
]

export function DocEditor({ initialBody, docId, spaceId, onSave }) {
  // spaceId is currently unused in the editor itself but kept on the props
  // for parity with future create-doc / share flows.
  void spaceId
  const fileInputRef = useRef(null)
  const lastSavedRef = useRef('')
  const [openMenu, setOpenMenu] = useState(null)
  const [styleOpen, setStyleOpen] = useState(false)
  const [colorOpen, setColorOpen] = useState(false)
  const [fontOpen, setFontOpen] = useState(false)
  const [sizeOpen, setSizeOpen] = useState(false)
  const styleRef = useRef(null)
  const colorRef = useRef(null)
  const fontRef = useRef(null)
  const sizeRef = useRef(null)
  const menuBarRef = useRef(null)

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Image,
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      Indent,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      // True page-aware pagination — content reflows into separate page
      // sheets once a line passes the bottom margin. Letter paper, ~0.5in
      // margins on every side (matches Word's "Narrow" preset). Header/
      // footer slots disabled. Borders set to 0 so the body-region inset
      // rectangle doesn't render.
      PaginationExtension.configure({
        defaultPaperSize: 'Letter',
        defaultMarginConfig: { top: 6.35, right: 6.35, bottom: 6.35, left: 6.35 },
        defaultPageBorders: { top: 0, right: 0, bottom: 0, left: 0 },
        pageAmendmentOptions: {
          enableHeader: false,
          enableFooter: false,
        },
      }),
      PageNode,
      HeaderFooterNode,
      BodyNode,
      TrimEmptyTrailingPage,
    ],
    content: parseBodyToHtml(initialBody),
    editorProps: {
      attributes: {
        class: 'doc-page-content outline-none min-h-[60vh]',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML()
      // Debounce save by hand: store latest, schedule timer.
      schedule(html)
      // Keep the cursor in view as the user types — prevents the typing
      // position from being scrolled off-screen as the doc grows.
      ed.commands.scrollIntoView()
    },
    onSelectionUpdate: ({ editor: ed }) => {
      // Mirror the same behaviour for arrow-key / click cursor moves.
      ed.commands.scrollIntoView()
    },
  })

  // Reload content when the document switches.
  useEffect(() => {
    if (!editor) return
    const html = parseBodyToHtml(initialBody)
    editor.commands.setContent(html, { emitUpdate: false })
    lastSavedRef.current = html
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId])

  // Debounced save 600ms after last keystroke.
  const saveTimerRef = useRef(null)
  function schedule(html) {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      if (html === lastSavedRef.current) return
      lastSavedRef.current = html
      onSave({ html })
    }, 600)
  }
  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
  }, [])


  // Outside-click + Escape close for popovers / menus.
  useEffect(() => {
    if (!styleOpen && !colorOpen && !fontOpen && !sizeOpen && openMenu === null) return
    function onClick(e) {
      const t = e.target
      if (styleOpen && !styleRef.current?.contains(t)) setStyleOpen(false)
      if (colorOpen && !colorRef.current?.contains(t)) setColorOpen(false)
      if (fontOpen && !fontRef.current?.contains(t)) setFontOpen(false)
      if (sizeOpen && !sizeRef.current?.contains(t)) setSizeOpen(false)
      if (openMenu !== null && !menuBarRef.current?.contains(t)) setOpenMenu(null)
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        setStyleOpen(false)
        setColorOpen(false)
        setFontOpen(false)
        setSizeOpen(false)
        setOpenMenu(null)
      }
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [styleOpen, colorOpen, fontOpen, sizeOpen, openMenu])

  if (!editor) {
    return (
      <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-10 text-center text-sm text-[color:var(--color-ink-muted)]">
        Loading editor…
      </div>
    )
  }

  function pick(action) {
    action()
    setOpenMenu(null)
  }

  // ---------- File ----------
  function downloadHtml() {
    const html = editor.getHTML()
    const fullHtml = `<!doctype html><html><head><meta charset="utf-8"/><title>Document</title></head><body>${html}</body></html>`
    triggerDownload(fullHtml, 'text/html', `document-${docId.slice(0, 8)}.html`)
  }
  function downloadText() {
    triggerDownload(editor.getText(), 'text/plain', `document-${docId.slice(0, 8)}.txt`)
  }


  async function shareWithLink() {
    const url = `${window.location.origin}/app/documents/${docId}`
    // Shared helper: falls back to a gesture-based execCommand copy when the
    // async Clipboard API is unavailable or refused. See lib/clipboard.js.
    if (!(await copyText(url))) {
      toast.error('Could not copy the link.')
      return
    }
    toast.success('Link copied — share it with anyone with workspace access.')
  }

  // ---------- Edit ----------
  function selectAll() {
    editor.chain().focus().selectAll().run()
  }
  async function pasteFromClipboard() {
    try {
      const text = await navigator.clipboard.readText()
      editor.chain().focus().insertContent(text).run()
    } catch {
      toast.error('Clipboard access denied. Use Ctrl/Cmd+V instead.')
    }
  }

  // ---------- Insert ----------
  function pickImageFile() {
    fileInputRef.current?.click()
  }
  function onImageChosen(file) {
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = String(reader.result)
      editor.chain().focus().setImage({ src: dataUrl }).run()
    }
    reader.onerror = () => toast.error('Could not read the image.')
    reader.readAsDataURL(file)
  }
  function insertHorizontalLine() {
    editor.chain().focus().setHorizontalRule().run()
  }
  function insertTodayDate() {
    const today = formatDateTime(new Date())
    editor.chain().focus().insertContent(today).run()
  }
  function insertTable() {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
  }
  function insertLink() {
    const previous = editor.getAttributes('link').href
    const url = window.prompt('Enter a URL', previous ?? '')
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  // ---------- Format ----------
  function setBlockStyle(level) {
    if (level === 0) editor.chain().focus().setParagraph().run()
    else editor.chain().focus().toggleHeading({ level }).run()
    setStyleOpen(false)
  }
  function setColor(hex) {
    editor.chain().focus().setColor(hex).run()
    setColorOpen(false)
  }

  // ---------- View ----------
  function toggleFullscreen() {
    if (document.fullscreenElement) {
      void document.exitFullscreen()
    } else {
      const root = document.querySelector('.doc-editor-root')
      void (root ?? document.documentElement).requestFullscreen()
    }
  }

  // ---------- Tools ----------
  function wordCount() {
    const text = editor.getText()
    const words = text.trim().split(/\s+/).filter(Boolean).length
    const chars = text.length
    toast.info(`${words} word${words === 1 ? '' : 's'} · ${chars} character${chars === 1 ? '' : 's'}`)
  }

  /**
   * Strip all formatting from the selection: reset block to paragraph, drop
   * every mark (bold/italic/underline/strike/link/textStyle), wipe font /
   * size / colour, reset alignment, and clear paragraph indent.
   */
  function clearFormatting() {
    const chain = editor.chain().focus()
    chain
      .clearNodes()
      .unsetAllMarks()
      .unsetMark('textStyle')
      .setTextAlign('left')
      .run()
    // Wipe paragraph indent attribute on all touched blocks.
    const types = ['paragraph', 'heading']
    types.forEach((t) => {
      try {
        editor.chain().focus().updateAttributes(t, { indent: 0 }).run()
      } catch {
        /* command may be unavailable for this node type — ignore */
      }
    })
  }

  return (
    <div className="doc-editor-root rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] h-full flex flex-col min-h-0 overflow-hidden">
      {/* Hidden file input for Insert → Image */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onImageChosen(f)
          if (fileInputRef.current) fileInputRef.current.value = ''
        }}
      />

      {/* Chrome (menu bar + toolbar) — fixed at the top of the editor box;
          the page surface below has its own scroll. */}
      <div className="shrink-0 bg-[color:var(--color-card)] rounded-t-lg">

      {/* Top menu bar */}
      <div
        ref={menuBarRef}
        className="flex items-center gap-1 px-2 py-1 border-b border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-xs text-[color:var(--color-ink-muted)] relative"
      >
        <TopMenu label="File" isOpen={openMenu === 'file'} onToggle={() => setOpenMenu(openMenu === 'file' ? null : 'file')}>
          <DropItem icon={Download} label="Download as HTML" onClick={() => pick(downloadHtml)} />
          <DropItem icon={Download} label="Download as plain text" onClick={() => pick(downloadText)} />
          <DropDivider />
          <DropItem icon={Share2} label="Share with link" onClick={() => pick(shareWithLink)} />
          <DropDivider />
          <DropItem icon={Printer} label="Print" shortcut={shortcut('P')} onClick={() => pick(() => window.print())} />
        </TopMenu>

        <TopMenu label="Edit" isOpen={openMenu === 'edit'} onToggle={() => setOpenMenu(openMenu === 'edit' ? null : 'edit')}>
          <DropItem icon={Undo} label="Undo" shortcut={shortcut('Z')} onClick={() => pick(() => editor.chain().focus().undo().run())} />
          <DropItem icon={Redo} label="Redo" shortcut={shortcut('Z', true)} onClick={() => pick(() => editor.chain().focus().redo().run())} />
          <DropDivider />
          <DropItem icon={Scissors} label="Cut" shortcut={shortcut('X')} onClick={() => pick(() => document.execCommand('cut'))} />
          <DropItem icon={CopyIcon} label="Copy" shortcut={shortcut('C')} onClick={() => pick(() => document.execCommand('copy'))} />
          <DropItem icon={ClipboardPaste} label="Paste" shortcut={shortcut('V')} onClick={() => pick(pasteFromClipboard)} />
          <DropDivider />
          <DropItem icon={CheckSquare} label="Select all" shortcut={shortcut('A')} onClick={() => pick(selectAll)} />
          <DropItem icon={Search} label="Find" shortcut={shortcut('F')} onClick={() => pick(() => toast.info('Find — coming soon.'))} />
        </TopMenu>

        <TopMenu label="View" isOpen={openMenu === 'view'} onToggle={() => setOpenMenu(openMenu === 'view' ? null : 'view')}>
          <DropItem icon={Maximize} label="Toggle full screen" onClick={() => pick(toggleFullscreen)} />
        </TopMenu>

        <TopMenu label="Insert" isOpen={openMenu === 'insert'} onToggle={() => setOpenMenu(openMenu === 'insert' ? null : 'insert')}>
          <DropItem icon={ImageIcon} label="Image" onClick={() => pick(pickImageFile)} />
          <DropItem icon={LinkIcon} label="Link" shortcut={shortcut('K')} onClick={() => pick(insertLink)} />
          <DropItem icon={TableIcon} label="Table (3×3)" onClick={() => pick(insertTable)} />
          <DropItem icon={Minus} label="Horizontal line" onClick={() => pick(insertHorizontalLine)} />
          <DropItem icon={CalendarIcon} label="Today's date" onClick={() => pick(insertTodayDate)} />
          {editor.isActive('table') ? (
            <>
              <DropDivider />
              <DropItem
                icon={TableIcon}
                label="Add row above"
                onClick={() => pick(() => editor.chain().focus().addRowBefore().run())}
              />
              <DropItem
                icon={TableIcon}
                label="Add row below"
                onClick={() => pick(() => editor.chain().focus().addRowAfter().run())}
              />
              <DropItem
                icon={TableIcon}
                label="Add column left"
                onClick={() => pick(() => editor.chain().focus().addColumnBefore().run())}
              />
              <DropItem
                icon={TableIcon}
                label="Add column right"
                onClick={() => pick(() => editor.chain().focus().addColumnAfter().run())}
              />
              <DropDivider />
              <DropItem
                icon={Minus}
                label="Delete row"
                onClick={() => pick(() => editor.chain().focus().deleteRow().run())}
              />
              <DropItem
                icon={Minus}
                label="Delete column"
                onClick={() => pick(() => editor.chain().focus().deleteColumn().run())}
              />
              <DropItem
                icon={Trash2}
                label="Delete table"
                onClick={() => pick(() => editor.chain().focus().deleteTable().run())}
              />
            </>
          ) : null}
          {editor.isActive('image') ? (
            <>
              <DropDivider />
              <DropItem
                icon={ImageIcon}
                label="Image — Small (25%)"
                onClick={() => pick(() => editor.chain().focus().updateAttributes('image', { width: '25%', height: null }).run())}
              />
              <DropItem
                icon={ImageIcon}
                label="Image — Medium (50%)"
                onClick={() => pick(() => editor.chain().focus().updateAttributes('image', { width: '50%', height: null }).run())}
              />
              <DropItem
                icon={ImageIcon}
                label="Image — Large (75%)"
                onClick={() => pick(() => editor.chain().focus().updateAttributes('image', { width: '75%', height: null }).run())}
              />
              <DropItem
                icon={ImageIcon}
                label="Image — Full width (100%)"
                onClick={() => pick(() => editor.chain().focus().updateAttributes('image', { width: '100%', height: null }).run())}
              />
              <DropItem
                icon={ImageIcon}
                label="Image — Custom size…"
                onClick={() => pick(() => promptImageSize(editor))}
              />
              <DropItem
                icon={ImageIcon}
                label="Image — Original size"
                onClick={() => pick(() => editor.chain().focus().updateAttributes('image', { width: null, height: null }).run())}
              />
              <DropItem
                icon={Trash2}
                label="Delete image"
                onClick={() => pick(() => editor.chain().focus().deleteSelection().run())}
              />
            </>
          ) : null}
        </TopMenu>

        <TopMenu label="Format" isOpen={openMenu === 'format'} onToggle={() => setOpenMenu(openMenu === 'format' ? null : 'format')}>
          <DropItem icon={Bold} label="Bold" shortcut={shortcut('B')} onClick={() => pick(() => editor.chain().focus().toggleBold().run())} />
          <DropItem icon={Italic} label="Italic" shortcut={shortcut('I')} onClick={() => pick(() => editor.chain().focus().toggleItalic().run())} />
          <DropItem icon={UnderlineIcon} label="Underline" shortcut={shortcut('U')} onClick={() => pick(() => editor.chain().focus().toggleUnderline().run())} />
          <DropItem icon={Strikethrough} label="Strikethrough" onClick={() => pick(() => editor.chain().focus().toggleStrike().run())} />
          <DropDivider />
          <DropItem icon={AlignLeft} label="Align left" onClick={() => pick(() => editor.chain().focus().setTextAlign('left').run())} />
          <DropItem icon={AlignCenter} label="Align centre" onClick={() => pick(() => editor.chain().focus().setTextAlign('center').run())} />
          <DropItem icon={AlignRight} label="Align right" onClick={() => pick(() => editor.chain().focus().setTextAlign('right').run())} />
          <DropDivider />
          <DropItem icon={ListIcon} label="Bullet list" onClick={() => pick(() => editor.chain().focus().toggleBulletList().run())} />
          <DropItem icon={ListOrdered} label="Numbered list" onClick={() => pick(() => editor.chain().focus().toggleOrderedList().run())} />
          <DropDivider />
          <DropItem icon={RemoveFormatting} label="Clear formatting" onClick={() => pick(() => editor.chain().focus().clearNodes().unsetAllMarks().run())} />
        </TopMenu>

        <TopMenu label="Tools" isOpen={openMenu === 'tools'} onToggle={() => setOpenMenu(openMenu === 'tools' ? null : 'tools')}>
          <DropItem icon={Hash} label="Word count" onClick={() => pick(wordCount)} />
        </TopMenu>
      </div>

      {/* Formatting toolbar */}
      <div className="flex items-center gap-0.5 px-3 py-1.5 border-b border-[color:var(--color-rule)] bg-[color:var(--color-card)] flex-wrap">
        <ToolbarBtn icon={Undo} title="Undo" onClick={() => editor.chain().focus().undo().run()} />
        <ToolbarBtn icon={Redo} title="Redo" onClick={() => editor.chain().focus().redo().run()} />
        <Sep />

        <div ref={styleRef} className="relative">
          <button
            type="button"
            onClick={() => setStyleOpen((v) => !v)}
            className="h-8 px-3 inline-flex items-center gap-1 text-sm rounded hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            {currentStyleLabel(editor)}
            <ChevronDown size={12} />
          </button>
          {styleOpen ? (
            <div className="absolute left-0 top-full mt-1 z-30 w-48 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-xl">
              {STYLE_OPTIONS.map((s) => (
                <button
                  key={s.level}
                  type="button"
                  onClick={() => setBlockStyle(s.level)}
                  className="w-full text-left px-3 py-2 text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                  style={{ fontSize: s.size }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <Sep />

        {/* Font family */}
        <div ref={fontRef} className="relative">
          <button
            type="button"
            onClick={() => setFontOpen((v) => !v)}
            title="Font family"
            className="h-8 px-3 inline-flex items-center gap-1 text-sm rounded hover:bg-[color:var(--color-card-hover)] cursor-pointer min-w-[7rem]"
          >
            <span className="truncate flex-1 text-left">{currentFontLabel(editor)}</span>
            <ChevronDown size={12} />
          </button>
          {fontOpen ? (
            <div className="absolute left-0 top-full mt-1 z-30 w-48 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-xl max-h-72 overflow-y-auto">
              {FONT_FAMILIES.map((f) => (
                <button
                  key={f.label}
                  type="button"
                  onClick={() => {
                    editor.chain().focus().setFontFamily(f.value).run()
                    setFontOpen(false)
                  }}
                  className="w-full text-left px-3 py-2 text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer text-sm"
                  style={{ fontFamily: f.value }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* Font size */}
        <div ref={sizeRef} className="relative">
          <button
            type="button"
            onClick={() => setSizeOpen((v) => !v)}
            title="Font size"
            className="h-8 px-2 inline-flex items-center gap-1 text-sm rounded hover:bg-[color:var(--color-card-hover)] cursor-pointer min-w-[3.5rem]"
          >
            <span className="truncate flex-1 text-left">{currentFontSize(editor)}</span>
            <ChevronDown size={12} />
          </button>
          {sizeOpen ? (
            <div className="absolute left-0 top-full mt-1 z-30 w-24 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-xl max-h-72 overflow-y-auto">
              {FONT_SIZES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    editor.chain().focus().setFontSize(s).run()
                    setSizeOpen(false)
                  }}
                  className="w-full text-left px-3 py-2 text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer text-sm"
                >
                  {s.replace('px', '')}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <Sep />
        <ToolbarBtn icon={Bold} title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
        <ToolbarBtn icon={Italic} title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
        <ToolbarBtn icon={UnderlineIcon} title="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} />
        <ToolbarBtn icon={Strikethrough} title="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} />

        <div ref={colorRef} className="relative">
          <ToolbarBtn icon={Type} title="Text colour" onClick={() => setColorOpen((v) => !v)} />
          {colorOpen ? (
            <div
              className="absolute left-0 top-full mt-1 z-30 w-[11rem] p-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-xl"
              role="dialog"
              aria-label="Text colour"
            >
              <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2">
                Colour
              </p>
              <div
                className="grid gap-2"
                style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}
              >
                {TEXT_COLOURS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    aria-label={`Text colour ${c}`}
                    title={c}
                    className="h-5 w-5 rounded cursor-pointer ring-1 ring-inset ring-black/10 motion-safe:hover:scale-110 transition-transform"
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <Sep />
        <ToolbarBtn icon={LinkIcon} title="Insert link" active={editor.isActive('link')} onClick={insertLink} />
        <Sep />
        <ToolbarBtn icon={AlignLeft} title="Align left" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} />
        <ToolbarBtn icon={AlignCenter} title="Align centre" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} />
        <ToolbarBtn icon={AlignRight} title="Align right" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} />
        <Sep />
        <ToolbarBtn icon={ListIcon} title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
        <ToolbarBtn icon={ListOrdered} title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
        <ToolbarBtn
          icon={IndentDecrease}
          title="Decrease indent"
          onClick={() => {
            if (editor.isActive('listItem')) {
              editor.chain().focus().liftListItem('listItem').run()
            } else {
              bumpIndent(editor, -1)
            }
          }}
        />
        <ToolbarBtn
          icon={IndentIncrease}
          title="Increase indent"
          onClick={() => {
            if (editor.isActive('listItem')) {
              editor.chain().focus().sinkListItem('listItem').run()
            } else {
              bumpIndent(editor, 1)
            }
          }}
        />
        <Sep />
        <ToolbarBtn
          icon={RemoveFormatting}
          title="Clear formatting"
          onClick={clearFormatting}
        />
      </div>

      </div>{/* /sticky chrome */}

      {/* Page surface — `tiptap-extension-pagination` renders each page as
          its own DOM element, so we just give EditorContent a centred wrapper
          and let the extension handle the page chrome (white sheet, shadow,
          1in margins) per page. */}
      <div className="flex-1 min-h-0 overflow-auto bg-[color:var(--color-canvas)] py-8 px-4 flex justify-center scrollbar-thin">
        <div className="w-full max-w-[10.5in] tiptap-pagination-wrapper">
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  )
}

// ---------- Top menu primitives ----------

function TopMenu({ label, isOpen, onToggle, children }) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        className={
          'px-2 py-1 rounded cursor-pointer ' +
          (isOpen
            ? 'bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-strong)]'
            : 'hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)]')
        }
      >
        {label}
      </button>
      {isOpen ? (
        <div className="absolute left-0 top-full mt-0.5 z-40 w-64 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-xl py-1">
          {children}
        </div>
      ) : null}
    </div>
  )
}

function DropItem({ icon: Icon, label, shortcut, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-[color:var(--color-card-hover)] cursor-pointer text-[color:var(--color-ink)]"
    >
      <Icon size={14} className="shrink-0 text-[color:var(--color-ink-muted)]" />
      <span className="flex-1 truncate">{label}</span>
      {shortcut ? (
        <span className="text-sm text-[color:var(--color-ink-muted)]">{shortcut}</span>
      ) : null}
    </button>
  )
}
function DropDivider() {
  return <div className="my-1 border-t border-[color:var(--color-rule)]" aria-hidden="true" />
}

// ---------- Toolbar primitives ----------

function ToolbarBtn({ icon: Icon, title, active, onClick }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={
        'h-8 w-8 rounded inline-flex items-center justify-center cursor-pointer ' +
        (active
          ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-dim)]'
          : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]')
      }
    >
      <Icon size={16} />
    </button>
  )
}
function Sep() {
  return <div className="mx-1 h-5 w-px bg-[color:var(--color-rule)]" aria-hidden="true" />
}

// ---------- Helpers ----------

function currentStyleLabel(editor) {
  if (editor.isActive('heading', { level: 1 })) return 'Heading 1'
  if (editor.isActive('heading', { level: 2 })) return 'Heading 2'
  if (editor.isActive('heading', { level: 3 })) return 'Heading 3'
  return 'Normal text'
}

function currentFontLabel(editor) {
  const family = editor.getAttributes('textStyle').fontFamily
  if (!family) return 'Arial'
  const known = FONT_FAMILIES.find((f) => f.value === family)
  return known?.label ?? family.split(',')[0].replace(/['"]/g, '').trim()
}

function currentFontSize(editor) {
  const size = editor.getAttributes('textStyle').fontSize
  if (!size) return '14'
  return size.replace('px', '')
}

function triggerDownload(content, mime, filename) {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
  toast.success('Download started.')
}

function parseBodyToHtml(body) {
  if (!body || typeof body !== 'object') return ''
  const obj = body
  if (typeof obj.html === 'string') return obj.html
  if (typeof obj.text === 'string') {
    return obj.text
      .split(/\r?\n/)
      .map((line) => (line.trim() ? `<p>${escapeHtml(line)}</p>` : '<p></p>'))
      .join('')
  }
  return ''
}

function escapeHtml(s) {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  )
}
